import React, { useEffect, useState, useMemo } from 'react';
import { newsAPI } from '../../lib/api';
import { Newspaper, ExternalLink, Clock, Filter, Search } from 'lucide-react';

const CATEGORIES = [
  { key: 'general',  label: 'Top News',  source: 'finnhub' },
  { key: 'crypto',   label: 'Crypto',    source: 'finnhub' },
  { key: 'forex',    label: 'Forex',     source: 'finnhub' },
  { key: 'merger',   label: 'M&A',       source: 'finnhub' },
  { key: 'rss-all',  label: 'RSS Feeds', source: 'rss' }
];

const QUICK_SYMBOLS = ['AAPL', 'TSLA', 'MSFT', 'NVDA', 'AMZN', 'GOOGL', 'META', 'JPM'];

const timeAgo = (ts) => {
  if (!ts) return '';
  const diff = Math.floor((Date.now() / 1000 - ts));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const News = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState(CATEGORIES[0]);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [customSymbol, setCustomSymbol] = useState('');

  const load = async (category, symbol) => {
    setLoading(true);
    try {
      let data;
      if (symbol) {
        data = await newsAPI.companyNews(symbol, 30);
      } else if (category.source === 'rss') {
        data = await newsAPI.latest(40);
      } else {
        data = await newsAPI.finnhub(category.key, 40);
      }
      setItems(data);
    } catch (e) {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(cat, symbolFilter); }, [cat, symbolFilter]);

  // auto-refresh every 2 min
  useEffect(() => {
    const iv = setInterval(() => load(cat, symbolFilter), 120000);
    return () => clearInterval(iv);
  }, [cat, symbolFilter]);

  const filtered = useMemo(() => {
    if (!searchInput.trim()) return items;
    const q = searchInput.toLowerCase();
    return items.filter(n => (n.title || '').toLowerCase().includes(q) || (n.body || '').toLowerCase().includes(q));
  }, [items, searchInput]);

  const setSymbol = (s) => {
    setSymbolFilter(s);
    setCustomSymbol('');
  };

  const submitCustom = (e) => {
    e.preventDefault();
    const v = customSymbol.trim().toUpperCase();
    if (v) setSymbolFilter(v);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Newspaper size={22} className="text-[#00e676]"/> Market News
        </h1>
        <p className="text-sm text-gray-400">Live market headlines powered by Finnhub + RSS aggregation. Auto-refreshes every 2 minutes.</p>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter size={14} className="text-gray-500"/>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => { setCat(c); setSymbolFilter(''); }} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${!symbolFilter && cat.key === c.key ? 'bg-[#00e676] text-black' : 'text-gray-400 border border-[#1a2823] hover:border-[#00e676]/40'}`}>{c.label}</button>
        ))}
      </div>

      {/* Symbol filter */}
      <div className="p-4 rounded-xl border border-[#1a2823] bg-[#0a1210] flex flex-wrap items-center gap-3">
        <div className="text-xs text-gray-400 uppercase tracking-wider">Filter by Symbol:</div>
        {QUICK_SYMBOLS.map(s => (
          <button key={s} onClick={() => setSymbol(s)} className={`px-2.5 py-1 rounded text-xs font-semibold transition ${symbolFilter === s ? 'bg-[#00e676] text-black' : 'border border-[#1a2823] text-gray-300 hover:border-[#00e676]/40 hover:text-white'}`}>{s}</button>
        ))}
        <form onSubmit={submitCustom} className="flex items-center gap-2">
          <input value={customSymbol} onChange={(e) => setCustomSymbol(e.target.value)} placeholder="Enter symbol (e.g. AMD)" className="w-40 bg-[#050807] border border-[#1a2823] rounded px-3 py-1 text-xs text-white focus:outline-none focus:border-[#00e676]"/>
          <button type="submit" className="px-3 py-1 rounded bg-[#00e676]/15 border border-[#00e676]/30 text-[#00e676] text-xs font-semibold hover:bg-[#00e676]/25">Load</button>
        </form>
        {symbolFilter && (
          <button onClick={() => setSymbolFilter('')} className="ml-auto text-xs text-gray-400 hover:text-red-400">Clear symbol · {symbolFilter}</button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
        <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search headlines & summaries..." className="w-full bg-[#0a1210] border border-[#1a2823] rounded-lg pl-9 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-[#00e676]"/>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading news…</div>}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(n => (
          <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="group p-4 rounded-xl border border-[#1a2823] bg-[#0a1210] hover:border-[#00e676]/40 transition">
            {n.imageurl && (
              <div className="aspect-video mb-3 overflow-hidden rounded-lg bg-[#050807]">
                <img src={n.imageurl} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = 'none'; }}/>
              </div>
            )}
            <div className="flex items-center gap-2 mb-2 text-[10px] flex-wrap">
              <span className="px-2 py-0.5 rounded bg-[#00e676]/10 text-[#00e676] font-semibold">{n.source_info?.name || n.source}</span>
              {n.related && <span className="px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 font-semibold">{n.related.split(',')[0]}</span>}
              <span className="text-gray-500 flex items-center gap-1"><Clock size={10}/>{timeAgo(n.published_on)}</span>
            </div>
            <h3 className="text-sm font-bold text-white leading-snug mb-2 line-clamp-3 group-hover:text-[#00e676] transition">{n.title}</h3>
            <p className="text-xs text-gray-400 leading-relaxed line-clamp-3">{(n.body || '').substring(0, 180)}…</p>
            <div className="mt-3 flex items-center gap-1 text-xs text-[#00e676] font-semibold">
              Read more <ExternalLink size={11}/>
            </div>
          </a>
        ))}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="py-16 text-center text-sm text-gray-500">
          {symbolFilter ? `No news found for ${symbolFilter}` : 'No news found. Try a different category.'}
        </div>
      )}
    </div>
  );
};

export default News;
